import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { ArrowLeft, Users, Gamepad2, Trophy, TrendingUp, Calendar, Flame, Lightbulb, Check, X, Clock } from 'lucide-react';

const ADMIN_EMAIL = 'Laremy@gmail.com';

const AdminDashboard = ({ user, onBack }) => {
  const [stats, setStats] = useState(null);
  const [deckIdeas, setDeckIdeas] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check admin access
  const isAdmin = user?.email?.toLowerCase() === ADMIN_EMAIL.toLowerCase();

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    if (!isAdmin) return;

    const fetchData = async () => {
      try {
        // Fetch stats
        const statsResponse = await fetch(`${backendUrl}/api/admin/stats`);
        if (statsResponse.ok) {
          const statsData = await statsResponse.json();
          setStats(statsData);
        }

        // Fetch deck ideas
        const ideasResponse = await fetch(`${backendUrl}/api/deck-ideas`);
        if (ideasResponse.ok) {
          const ideasData = await ideasResponse.json();
          setDeckIdeas(ideasData);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [isAdmin, backendUrl]);

  const updateIdeaStatus = async (ideaId, status) => {
    try {
      const response = await fetch(`${backendUrl}/api/deck-ideas/${ideaId}/status?status=${status}`, {
        method: 'PUT'
      });
      if (response.ok) {
        setDeckIdeas(ideas => 
          ideas.map(idea => 
            idea.idea_id === ideaId ? { ...idea, status } : idea
          )
        );
      }
    } catch (err) {
      console.error('Failed to update status:', err);
    }
  };

  // Not authorized
  if (!user || !isAdmin) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-red-900/50 border-red-500 p-8 text-center max-w-md">
          <h1 className="text-2xl font-bold text-white mb-4">🔒 Access Denied</h1>
          <p className="text-gray-300 mb-6">You don't have permission to view this page.</p>
          <Button onClick={onBack} className="bg-white/20 hover:bg-white/30 text-white">
            ← Back to Game
          </Button>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-white"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
        <Card className="bg-red-900/50 border-red-500 p-8 text-center">
          <h1 className="text-xl font-bold text-white mb-4">Error Loading Stats</h1>
          <p className="text-gray-300">{error}</p>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4 sm:p-8">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <Button
            onClick={onBack}
            variant="ghost"
            className="text-white hover:bg-white/10"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Game
          </Button>
          <h1 className="text-2xl sm:text-3xl font-bold text-white">📊 Admin Dashboard</h1>
          <div className="w-24"></div>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Card className="bg-blue-600/30 border-blue-500/50 p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <Users className="w-8 h-8 text-blue-400" />
              <div>
                <p className="text-blue-200 text-sm">Total Users</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">{stats?.summary?.total_users || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-green-600/30 border-green-500/50 p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <Gamepad2 className="w-8 h-8 text-green-400" />
              <div>
                <p className="text-green-200 text-sm">Games Played</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">{stats?.summary?.total_games || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-orange-600/30 border-orange-500/50 p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <Flame className="w-8 h-8 text-orange-400" />
              <div>
                <p className="text-orange-200 text-sm">Daily Challenges</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">{stats?.summary?.daily_challenge_games || 0}</p>
              </div>
            </div>
          </Card>

          <Card className="bg-purple-600/30 border-purple-500/50 p-4 sm:p-6">
            <div className="flex items-center gap-3">
              <TrendingUp className="w-8 h-8 text-purple-400" />
              <div>
                <p className="text-purple-200 text-sm">New Users (7d)</p>
                <p className="text-2xl sm:text-3xl font-bold text-white">{stats?.summary?.recent_signups_7d || 0}</p>
              </div>
            </div>
          </Card>
        </div>

        {/* Two Column Layout */}
        <div className="grid lg:grid-cols-2 gap-6">
          {/* Popular Decks */}
          <Card className="bg-white/5 border-white/10 p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              Most Popular Decks
            </h2>
            <div className="space-y-3">
              {stats?.popular_decks?.length > 0 ? (
                stats.popular_decks.map((deck, index) => (
                  <div key={deck.deck_type} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-yellow-500 text-black' :
                        index === 1 ? 'bg-gray-400 text-black' :
                        index === 2 ? 'bg-orange-600 text-white' :
                        'bg-white/20 text-white'
                      }`}>
                        {index + 1}
                      </span>
                      <span className="text-white">{deck.deck_name}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium">{deck.plays} plays</p>
                      <p className="text-gray-400 text-sm">avg: {deck.avg_score}</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-center py-4">No games played yet</p>
              )}
            </div>
          </Card>

          {/* Top Players */}
          <Card className="bg-white/5 border-white/10 p-6">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Users className="w-5 h-5 text-cyan-400" />
              Top Players (Total Score)
            </h2>
            <div className="space-y-3">
              {stats?.top_players?.length > 0 ? (
                stats.top_players.map((player, index) => (
                  <div key={player.nickname} className="flex items-center justify-between bg-white/5 rounded-lg p-3">
                    <div className="flex items-center gap-3">
                      <span className={`w-6 h-6 rounded-full flex items-center justify-center text-sm font-bold ${
                        index === 0 ? 'bg-yellow-500 text-black' :
                        index === 1 ? 'bg-gray-400 text-black' :
                        index === 2 ? 'bg-orange-600 text-white' :
                        'bg-white/20 text-white'
                      }`}>
                        {index + 1}
                      </span>
                      <span className="text-white">{player.nickname}</span>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium">{player.total_score} pts</p>
                      <p className="text-gray-400 text-sm">{player.games_played} games</p>
                    </div>
                  </div>
                ))
              ) : (
                <p className="text-gray-400 text-center py-4">No players yet</p>
              )}
            </div>
          </Card>

          {/* Daily Games Chart (simple) */}
          <Card className="bg-white/5 border-white/10 p-6 lg:col-span-2">
            <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
              <Calendar className="w-5 h-5 text-green-400" />
              Games Per Day (Last 7 Days)
            </h2>
            {stats?.daily_games?.length > 0 ? (
              <div className="flex items-end justify-between gap-2 h-40">
                {stats.daily_games.map((day) => {
                  const maxGames = Math.max(...stats.daily_games.map(d => d.games));
                  const height = maxGames > 0 ? (day.games / maxGames) * 100 : 0;
                  return (
                    <div key={day.date} className="flex-1 flex flex-col items-center gap-2">
                      <span className="text-white text-sm font-medium">{day.games}</span>
                      <div 
                        className="w-full bg-gradient-to-t from-green-600 to-green-400 rounded-t-lg transition-all"
                        style={{ height: `${Math.max(height, 5)}%` }}
                      />
                      <span className="text-gray-400 text-xs">{day.date.slice(5)}</span>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-400 text-center py-8">No game data for the last 7 days</p>
            )}
          </Card>
        </div>

        {/* Deck Ideas Section */}
        <Card className="bg-white/5 border-white/10 p-6 mt-6">
          <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
            <Lightbulb className="w-5 h-5 text-yellow-400" />
            Deck Ideas ({deckIdeas.filter(i => i.status === 'pending').length} pending)
          </h2>
          <div className="space-y-3">
            {deckIdeas.length > 0 ? (
              deckIdeas.map((idea) => (
                <div key={idea.idea_id} className={`rounded-lg p-4 ${
                  idea.status === 'pending' ? 'bg-yellow-900/20 border border-yellow-500/30' :
                  idea.status === 'approved' ? 'bg-green-900/20 border border-green-500/30' :
                  idea.status === 'created' ? 'bg-blue-900/20 border border-blue-500/30' :
                  'bg-red-900/20 border border-red-500/30'
                }`}>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="text-white font-medium">{idea.deck_name}</h3>
                        <span className={`text-xs px-2 py-0.5 rounded-full ${
                          idea.status === 'pending' ? 'bg-yellow-500/30 text-yellow-300' :
                          idea.status === 'approved' ? 'bg-green-500/30 text-green-300' :
                          idea.status === 'created' ? 'bg-blue-500/30 text-blue-300' :
                          'bg-red-500/30 text-red-300'
                        }`}>
                          {idea.status}
                        </span>
                      </div>
                      <p className="text-gray-400 text-sm">
                        <span className="text-purple-400">{idea.category}</span> • Ranked by: {idea.ranked_by}
                      </p>
                      <p className="text-gray-500 text-xs mt-1">
                        Submitted by {idea.submitter_name} • {new Date(idea.created_at).toLocaleDateString()}
                      </p>
                    </div>
                    {idea.status === 'pending' && (
                      <div className="flex gap-2 ml-4">
                        <Button
                          size="sm"
                          onClick={() => updateIdeaStatus(idea.idea_id, 'approved')}
                          className="bg-green-600 hover:bg-green-700 text-white h-8 w-8 p-0"
                          title="Approve"
                        >
                          <Check className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          onClick={() => updateIdeaStatus(idea.idea_id, 'rejected')}
                          className="bg-red-600 hover:bg-red-700 text-white h-8 w-8 p-0"
                          title="Reject"
                        >
                          <X className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                    {idea.status === 'approved' && (
                      <Button
                        size="sm"
                        onClick={() => updateIdeaStatus(idea.idea_id, 'created')}
                        className="bg-blue-600 hover:bg-blue-700 text-white text-xs"
                      >
                        Mark Created
                      </Button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <p className="text-gray-400 text-center py-4">No deck ideas submitted yet</p>
            )}
          </div>
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-gray-500 text-sm">
          Last updated: {new Date().toLocaleString()}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;
